'use client';

/**
 * RetouchModalContent Component
 *
 * Content component for the returning visitor discount modal.
 * Designed to be used with the shared Modal component.
 *
 * Uses the same UI layout as DiwaliPopupModal - two-column layout
 * with image on left and text content on right.
 *
 * Shows a 10% discount offer to users visiting Ring pages 4+ times.
 */

import React from 'react';
import { CrossDark } from 'assets/icons';
import { CustomImage as Image } from 'components/shared/custom-image';
import { useTranslations } from 'next-intl';
import styles from './RetouchModalContent.module.css';

interface RetouchModalContentProps {
  /** Called when user clicks "Claim My Discount" */
  onClaimDiscount: () => void;
  /** Called when user closes the modal */
  onClose: () => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * RetouchModalContent Component
 *
 * Content for the returning visitor discount modal.
 * Displays after user visits Ring pages 4+ times.
 *
 * Uses DiwaliPopupModal-style two-column layout with image and text.
 *
 * Includes its own close button since the shared Modal's close button
 * doesn't allow intercepting the close event to call markModalShown().
 *
 * Note: Backdrop clicks will hide the modal but NOT mark it as shown,
 * allowing the modal to appear again on future qualifying visits.
 * Only explicit interactions (close button, CTA) mark it as shown.
 */
export const RetouchModalContent: React.FC<RetouchModalContentProps> = ({
  onClaimDiscount,
  onClose,
}) => {
  const t = useTranslations('common');
  return (
    <div className={styles.container}>
      <button
        className={styles.closeButton}
        onClick={onClose}
        aria-label={t('retouchFlow.closeButton.ariaLabel.closeDiscountModal')}
        type="button"
      >
        <CrossDark />
      </button>

      <div className={styles.imageContainer}>
        <Image src="web_v2/offer_modal/brg-modal-sq-hand-2.png" fill alt="" />
      </div>

      <div className={styles.textContainer}>
        {/* <Label>STILL THINKING?</Label> */}
        <h3 className={styles.headline}>{t('retouchFlow.headline.makeItYours')}</h3>
        <p className={styles.description}>
          {t.rich('retouchFlow.description.enjoyOff', {
            bold: (chunks) => <span className={styles.boldText}>{chunks}</span>,
            br: () => <br />,
          })}
        </p>

        <button
          className={`${styles.ctaButton} gtm`}
          onClick={onClaimDiscount}
          type="button"
          data-gtm-action="retouch_flow_claim_discount"
        >
          {t('retouchFlow.button.unlockExclusivePricing')}
        </button>
      </div>
    </div>
  );
};

export default RetouchModalContent;
